import { AxiosError, AxiosResponse } from 'axios';
import { useSnackbar } from 'notistack';
import { useCallback, useContext } from 'react';
import LicenseContext from 'src/contexts/LicenseContext';
import PlansContext, { OrderSelectionContext } from 'src/contexts/PlansContext';
import { AllocateHistoryEntity } from 'src/entities/AllocateHistory.entity';
import { PlanningSettingRequest } from 'src/entities/PlanningHistory.entity';
import { PlanningResponseEntity, AsyncPlanningResponseEntity } from 'src/entities/planningResponseEntity';
import { PlanningsOperationDelivery } from 'src/models/PlanningsOperationGroup.model';

import { usePlanningsOrdersCache } from './usePlanningsCache';
import { useMutationPlanning, useMutationPlanningV5 } from './useQueryPlanning';

interface PlanningRequestHookParams {
  dispatchPlanning?: (action: { type: string }) => void;
  setAsyncPlanningResponse: (response: AsyncPlanningResponseEntity | null) => void;
  generateRandomString: (charCount?: number) => string;
  resetQuery: () => void;
  normalPlanning?: ReturnType<typeof useMutationPlanning>['normalPlanning'];
  normalPlanningV5?: ReturnType<typeof useMutationPlanningV5>['normalPlanning'];
}

export const usePlanningRequest = ({
  dispatchPlanning,
  setAsyncPlanningResponse,
  generateRandomString,
  resetQuery,
  normalPlanning,
  normalPlanningV5,
}: PlanningRequestHookParams) => {
  const licenseContext = useContext(LicenseContext);
  const plansContext = useContext(PlansContext);
  const orderSelection = useContext(OrderSelectionContext);
  const ordersCache = usePlanningsOrdersCache();

  const {
    deliveryEntities, selectedCycleIndexes, truckDisplayStatus, selectedGroupEntities,
    allDeliverySelected, orderSearchKw, currentlyOrderSearching, orderSearchConditions,
    planningEntity, latestAlgorithmRequestVersion, planningsOperationDeliveryByDeliveryIdEntity,
    refreshRunningEntities, handlePlanningResponse, handleErrorResponse,
    driverEntities, truckEntities,
    setIsLoading, setCalculating,
  } = plansContext;

  const {
    removeOrderIds, addAllocatedOrderIds,
    removeAllocatedOrderIds, bulkRemoveSelectedOrderIds, addOrderIds, bulkAddSelectedOrderIds,
  } = orderSelection;

  const { enqueueSnackbar } = useSnackbar();

  const planningRequest = useCallback((
    requestDeliveryIds: number[],
    requestOrderIds: number[]
  ) => {
    plansContext.resetPlanningsNotAllocReasons();
    plansContext.setNotAllocReasonState({ sessionId: null, notAllocOrderLength: 0, disabledOrders: [] });

    const shiftIds = requestDeliveryIds
        .map((it) => {
          const entity = deliveryEntities.find((e) => e.id === it);
          return entity ? entity.shiftId : null;
        });

    if (shiftIds.length === 0) {
      enqueueSnackbar('配車可能なシフトがありません');
      return;
    }

    const allocateHistoryEntity: AllocateHistoryEntity = {
      version: generateRandomString(20),
      companyId: licenseContext.config?.selected_company_id,
      actAt: new Date(),
      action: '自動配車',
      message: '自動配車を実行しました',
      deliveryEntities,
    };

    plansContext.addAllocateHistoryEntity(allocateHistoryEntity);

    enqueueSnackbar('自動配車を開始します');

    plansContext.setCalculating(true);

    plansContext.setIsLoading(true);

    const deliveryOperations = requestDeliveryIds.map((deliveryId) => {
      const delivery: PlanningsOperationDelivery = planningsOperationDeliveryByDeliveryIdEntity[deliveryId];
      delivery.cycles.forEach((it) => {
        it.allOperations().forEach((ops) => {
          ops.cycle = it.cycleIndex;
        });
      });
      return {
        deliveryId,
        operations: delivery.allOperations().map((it) => ({
          id: it.id,
          cycle: it.cycle,
          orderId: it.orderId,
          action: it.action,
        }))
      };
    });

    const selectedCycleIndexesOnlyAlloced = selectedCycleIndexes.map((it) => {
      const delivery = planningsOperationDeliveryByDeliveryIdEntity[it.deliveryId];
      if (delivery) {
        const selectedCycles = delivery.cycles.filter((c) => it.cycleIndexes.includes(c.cycleIndex));
        if (selectedCycles.every((c) => c.empty)) {
          return {
            ...it,
            cycleIndexes: [it.cycleIndexes[0]], // すべて未割り当てなら最初の回転だけにする
          };
        }
        return {
          ...it,
          cycleIndexes: selectedCycles.filter((c) => !c.empty).map((c) => c.cycleIndex), // 割り当て済みが含まれる場合は割り当て済みだけにする
        };
      }
      return it;
    });
    const deliveryConditions = {
      truckDisplayStatus,
      selectedGroupIds: selectedGroupEntities.map((it) => it.id),
      selectedGroups: selectedGroupEntities,
      allDeliverySelected,
      selectedCycleIndexes,
      selectedDeliveries: selectedCycleIndexes.map((it) => {
        const { deliveryId } = it;
        const delivery = deliveryEntities.find((deli) => deli.id === deliveryId);
        if (!delivery) return null;

        const truck = truckEntities.find((t) => t.id === delivery.truckId);
        const driver = driverEntities.find((d) => d.id === delivery.driverId);
        return {
          deliveryId,
          truckId: truck.id,
          licensePlateValue: truck.licensePlateValue,
          driverId: driver.id,
          driverName: driver.name,
        };
      })
    };
    const orderConditions = {
      orderSearchKw,
      orderSearchConditions: currentlyOrderSearching ? orderSearchConditions : [],
    };
    const planningConditions = plansContext.planningSettings();
    const planningSetting: PlanningSettingRequest = {
      deliveryConditions,
      orderConditions,
      planningConditions,
    };

    let async = false;
    (normalPlanning || normalPlanningV5).mutate(
      {
        shiftIds,
        orderIds: requestOrderIds,
        planning: planningEntity,
        latestAlgorithmRequestVersion,
        requestAsync: true,
        selectedCycleIndexes: selectedCycleIndexesOnlyAlloced,
        deliveryOperations,
        planningSetting,
      },
      {
        onSuccess: (data: AxiosResponse<PlanningResponseEntity>) => {
          if (dispatchPlanning) {
            dispatchPlanning({
              type: 'resetRestrictedInsertPosition',
            });
          }

          if (removeOrderIds) {
            removeOrderIds(requestOrderIds);
          }

          addAllocatedOrderIds(requestOrderIds);
          if (data.data.splitted_orders) {
            const splittedOrders = data.data.splitted_orders;

            ordersCache.removeOrderByIds(splittedOrders.original_order_ids);
            ordersCache.addOrders(splittedOrders.split_orders);

            removeAllocatedOrderIds(splittedOrders.original_order_ids);
            removeOrderIds(splittedOrders.original_order_ids);
            bulkRemoveSelectedOrderIds(splittedOrders.original_order_ids);

            addAllocatedOrderIds(splittedOrders.split_order_ids);
            addOrderIds(splittedOrders.split_order_ids);
            bulkAddSelectedOrderIds(splittedOrders.split_order_ids);
          }

          if (data.data.status === 'queued') {
            async = true;
            const entity: AsyncPlanningResponseEntity = data.data;
            setAsyncPlanningResponse(entity);
            enqueueSnackbar('自動配車を実行中です。完了までしばらくお待ち下さい。');
            refreshRunningEntities();
          } else {
            handlePlanningResponse(data.data, '自動配車が完了しました', resetQuery);
          }
        },
        onError: (e: AxiosError<{ message: string }>) => {
          handleErrorResponse(e, resetQuery);
        },
        onSettled: () => {
          if (async) return;

          setIsLoading(false);
          setCalculating(false);
        }
      }
    );
  }, [generateRandomString, licenseContext.config?.selected_company_id, deliveryEntities, enqueueSnackbar, plansContext, selectedCycleIndexes, truckDisplayStatus, selectedGroupEntities, allDeliverySelected, orderSearchKw, currentlyOrderSearching, orderSearchConditions, normalPlanning, planningEntity, latestAlgorithmRequestVersion, planningsOperationDeliveryByDeliveryIdEntity, truckEntities, driverEntities, dispatchPlanning, removeOrderIds, setAsyncPlanningResponse, refreshRunningEntities, handlePlanningResponse, handleErrorResponse, resetQuery]);

  return { planningRequest };
};
